import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import {
    ALERT_TYPE,
    TCI_APP_FEATURE_TYPE,
    ALERT_TYPE_LABEL,
    ITCIHook,
    ITCINotification,
    ITCIRule,
    TCITenantAppType,
    strEnum, USER_ROLES, ITCIApplication
} from "@tci/common";
import * as lodash from "lodash";
import { Dialog, SelectItem } from "primeng/primeng";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { NgAppFeatureToggleService } from "../../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { TciTenantRouteDeActivator } from "../../../ng-tenant/tci-tenant-route-de-activator.service";
import { NgPaginatorComponent } from "../ng-paginator/ng-paginator.component";
import { NgToastrService } from "../ng-toastr/ng-toastr.service";
import { NgAlertService } from "./ng-alert.service";
import { debounceTime, filter, map, distinctUntilChanged } from "rxjs/operators";
import {NgUserService} from "../../../ng-shared/ng-services/ng-user.service";

// Hooks is for Env&tools page only
const ALERT_LEFT_MENU = strEnum(["Configuration", "Hooks", "Notifications"]);
type ALERT_LEFT_MENU = keyof typeof ALERT_LEFT_MENU;

const GAL_SORTABLE_ROWS = {
    "ALERT_NAME": "alertName",
    "APP_ID": "appId",
    "INSTANCE_ID": "instanceId",
};

const GAL_RULE_TYPE_LIST = [{
        label: ALERT_TYPE_LABEL.TIB_APP_CPU,
        value: ALERT_TYPE.TIB_APP_CPU
    }, {
        label: ALERT_TYPE_LABEL.TIB_APP_MEMORY,
        value: ALERT_TYPE.TIB_APP_MEMORY
    }, {
        label: ALERT_TYPE_LABEL.TIB_APP_HIGH_FAULTED_EXECUTIONS,
        value: ALERT_TYPE.TIB_APP_HIGH_FAULTED_EXECUTIONS
    }, {
        label: ALERT_TYPE_LABEL.TIB_APP_INSTANCE_COUNT_0,
        value: ALERT_TYPE.TIB_APP_INSTANCE_COUNT_0
    // }, {
    //     label: ALERT_TYPE_LABEL.TIB_APP_XYZ,
    //     value: ALERT_TYPE.TIB_APP_XYZ
    }];

@Component({
    selector: "ng-alert",
    templateUrl: "./ng-alert.component.html",
    styleUrls: ["./ng-alert.component.less"]
})
export class NgAlertComponent implements OnInit, OnDestroy {
    @ViewChild("pager") pager: NgPaginatorComponent;
    @ViewChild("dialogDeleteRule") dialogDeleteRule: Dialog;
    @ViewChild("dialogDeleteHook") dialogDeleteHook: Dialog;
    @ViewChild("dialogAddEditHook") dialogAddEditHook: Dialog;
    @ViewChild("dialogLeavePage") dialogLeavePage: Dialog;
    @Input() appId = "";
    @Input() routeId = "";
    @Input() application: ITCIApplication = null;
    private currentRoute: string;
    private activeTab: ALERT_LEFT_MENU;
    private pendingActiveTab: ALERT_LEFT_MENU;
    private subscriptions: Subscription[] = [];
    private canDeactivateDialogResponse$: Subject<boolean> = new Subject();
    isLoading = false;
    alertLeftMenu = ALERT_LEFT_MENU;
    alertTabList = [];
    showDeleteRuleConfirmation = false;
    showDeleteHookConfirmation = false;
    showAddEditHookDialog = false;
    showLeaveConfirmation = false;
    ruleTypeList: SelectItem[] = [];
    ruleList: ITCIRule[] = [];
    deletingRule: ITCIRule;
    hookList: ITCIHook[] = [];
    editingHook: ITCIHook;
    deletingHook: ITCIHook;
    searchNotificationFilterCtrl = new FormControl();
    originalNotificationList: ITCINotification[] = [];
    notificationList: ITCINotification[] = [];
    isAdminOrOwner = true;

    // Pagination
    pageStart = 0;
    pages = 20;
    paginatedRows$: BehaviorSubject<ITCINotification[]> = new BehaviorSubject([]);

    // for sorting
    SORTABLE_ROWS = GAL_SORTABLE_ROWS;
    sortedBy: string = GAL_SORTABLE_ROWS.ALERT_NAME;
    sortAsc = true;

    constructor(
                private router: Router,
                private route: ActivatedRoute,
                private routeDeactivator: TciTenantRouteDeActivator,
                private appFeatureToggleService: NgAppFeatureToggleService,
                private alertService: NgAlertService,
                private userService: NgUserService,
                private toastr: NgToastrService) {
    }

    ngOnInit() {
        this.currentRoute = this.routeId;
        this.routeDeactivator.addRouteId(this.currentRoute, this);
        this.alertTabList = Object.keys(this.alertLeftMenu);

        // only show Configuration and Notifications for app detail page
        if (this.appId) {
            this.alertTabList = [ALERT_LEFT_MENU.Configuration, ALERT_LEFT_MENU.Notifications];
        } else {
            // featureToggle default is not enabled, so only show Hooks for Env&tools page
            // when featureToggle is enabled, show all 3 tabs
            this.appFeatureToggleService.getAppFeatures()
                .subscribe(resFeature => {
                    if (resFeature[TCI_APP_FEATURE_TYPE.ENV_ALERT].enabled === false) {
                        this.alertTabList = [ALERT_LEFT_MENU.Hooks];
                    }
                });
        }
        this.subscriptions.push(
            this.route.params.pipe(
                debounceTime(300),
                filter(params => params["routeid"] === this.currentRoute),
                map(params => params["submenuleft"]),
                distinctUntilChanged()
            ).subscribe(tab => {
                // check if the active tab is in alertTabList
                if (this.alertTabList.indexOf(tab) > -1) {
                    this.doActiveTab(tab);
                } else {
                    this.routerNavigateTab(this.alertTabList[0]);
                }
            })
        );

        this.subscriptions.push(this.searchNotificationFilterCtrl.valueChanges
            .debounceTime(300)
            .subscribe(() => this.searchNotification()));

        this.ruleTypeList = lodash.cloneDeep(GAL_RULE_TYPE_LIST);

        this.userService.getUserInfo().subscribe(token => {
            // this.isAdminUser = token.rolX[0] === USER_ROLES.ADM;
            // For Env Tools page the user is always admin
            // For App Details the user can be admin  or owner of the app
            this.isAdminOrOwner = (USER_ROLES.ADM === token.rolX[0] || (USER_ROLES.USR === token.rolX[0] && this.application && this.application?.owner === token.uname));
        });
    }

    ngOnDestroy() {
        this.subscriptions
            .filter(s => s && !s.closed)
            .forEach(s => s.unsubscribe());
        // Unregister the activator
        this.routeDeactivator.removeRouteId(this.currentRoute);
    }

    // canDeactivate is called when user tries to navigate away from the page (include switching left menu tab and clicking on other links)
    canDeactivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        if (this.isRuleUnsaved()) {
            this.showAndCenterLeaveConfirmationDialog();
            return Observable.create(obs => {
                const sub = this.canDeactivateDialogResponse$
                    .do(() => this.showLeaveConfirmation = false)
                    .subscribe(res => {
                        if (res) { // Clean the changes
                            this.deleteEmptyRule();
                        }
                        obs.next(res);
                        obs.complete();
                    });
                this.subscriptions.push(sub);
            });
        }
        return Observable.of(true);
    }

    setActiveTab(tab: ALERT_LEFT_MENU) {
        // ignore if tab is already active
        if (this.activeTab === tab) {
            return;
        }
        if (this.isRuleUnsaved()) {
            this.showAndCenterLeaveConfirmationDialog();
            this.pendingActiveTab = tab;
            return;
        }
        this.doActiveTab(tab);
    }

    /**
     * leavePage is called when user clicks on "Leave" button in the popup
     * @param isForcedLeave
     *  - true: if user clicks on "Yes, leave" button
     *  - false: if user clicks on "No, stay" button
     */
    leavePage(isForcedLeave: boolean) {
        // switch left side menu tab
        if (this.pendingActiveTab) {
            this.showLeaveConfirmation = false;
            // if it is forced leave, delete the empty rule and switch tab
            if (isForcedLeave) {
                this.deleteEmptyRule();
                this.routerNavigateTab(this.pendingActiveTab);
            }
            this.pendingActiveTab = null;
        } else {
            // click on other links (top menu, app detail other tab, etc.)
            this.canDeactivateDialogResponse$.next(isForcedLeave);
        }
    }

    isActiveTab(tab: ALERT_LEFT_MENU) {
        return this.activeTab === tab;
    }

    // in env&tolls page, there is no appId
    private isInEnvPage(): boolean {
        return !this.appId;
    }

    private routerNavigateTab(tab: ALERT_LEFT_MENU) {
        this.router.navigate(
            [{ submenuleft: tab }],
            {
                relativeTo: this.route,
                preserveQueryParams: true,
                replaceUrl: true
            }
        );
    }

    private doActiveTab(tab: ALERT_LEFT_MENU) {
        this.toastr.clear();
        this.activeTab = tab;
        switch (tab) {
            case ALERT_LEFT_MENU.Configuration:
                this.getRuleList();
                break;
            case ALERT_LEFT_MENU.Hooks:
                this.getHookList();
                break;
            case ALERT_LEFT_MENU.Notifications:
                this.getNotificationList();
                break;
        }
    }

    private showAndCenterLeaveConfirmationDialog() {
        this.showLeaveConfirmation = true;
        this.dialogLeavePage.center();
    }

    // --- for rule tab: start---
    getRuleList(isKeepUnsavedRule: boolean = true) {
        this.isLoading = true;
        // revert the unsaved rule back to the rule list if it exists
        const unSavedRule = this.ruleList.find(rule => rule.ruleName === "");
        this.subscriptions.push(this.alertService.getRuleList(this.appId)
            .subscribe(
                res => {
                    if (unSavedRule && isKeepUnsavedRule) {
                        unSavedRule.thresholdList = [];
                        res.push(unSavedRule);
                        this.ruleList = this.formatRuleList(res);
                        this.formatRuleTypeList(this.ruleList);
                    } else {
                        this.ruleList = this.formatRuleList(res);
                    }
                    this.isLoading = false;
                },
                () => {
                    this.isLoading = false;
                    this.toastr.error("Failed to get rule list");
                }
            )
        );
    }

    changeRuleType(rule: ITCIRule) {
        // if (rule.ruleGroup === ALERT_TYPE.TIB_APP_XYZ) {
        //     // this is a special case, need to save it immediately.
        //     const specificRuleName = this.ruleList.find(item => item.ruleName && item.ruleGroup === rule.ruleGroup)?.ruleName;
        //     if (specificRuleName) {
        //         this.saveXyzRule(specificRuleName);
        //     }
        //     return;
        // }
        rule.thresholdList = this.getUsableThresholdDropdownList(this.ruleList, rule.ruleGroup as ALERT_TYPE);
        if (rule.ruleName) {
            if (rule.thresholdList.length === 0) {
                rule.thresholdList.push({
                    label: rule.threshold,
                    value: rule.threshold
                });
            }
        }
    }

    addNewRule() {
        // need to check if the unsaved rule is already in the list
        if (this.isRuleUnsaved()) {
            this.toastr.error("Please save the newly added rule first.");
            return;
        }
        if (this.ruleList.length && this.ruleList.findIndex(item => !item.isEnabled) === -1) {
            this.toastr.error("All rule has been enabled.");
            return;
        }
        this.formatRuleTypeList(this.ruleList);

        this.ruleList.push({
            ruleName: "",
            isEnabled: true,
            ruleGroup: "",
            description: "",
            threshold: ""
        });
    }

    // isXyzRule(ruleGroup: string): boolean {
    //     return ruleGroup === ALERT_TYPE.TIB_APP_XYZ;
    // }

    // saveXyzRule(ruleName: string) {
    //     this.isLoading = true;
    //     this.subscriptions.push(this.alertService.addAppRule(this.appId, ruleName)
    //         .subscribe(
    //             () => {
    //                 this.getRuleList(false);
    //                 this.toastr.success("Add alert rule successful.");
    //             },
    //             () => {
    //                 this.isLoading = false;
    //                 this.toastr.error("Add alert rule failed.");
    //             }
    //         )
    //     );
    // }

    saveRule(oldRuleName: string, ruleGroup: string, threshold: string) {
        // get ruleName from the list by threshold
        const newRuleName = this.ruleList.find(rule => rule.ruleGroup === ruleGroup && rule.threshold === threshold && !rule.isEnabled)?.ruleName;
        // if the old ruleName is the same as the new ruleName or newRuleName does not exist, no any action
        if (oldRuleName === newRuleName || !newRuleName) {
            return ;
        }
        this.isLoading = true;
        // for the new rule, it needs to call addAppRule
        if (oldRuleName === "") {
            this.subscriptions.push(this.alertService.addAppRule(this.appId, newRuleName)
                .subscribe(
                    () => {
                        this.getRuleList(false);
                        this.toastr.success("Add alert rule successful.");
                    },
                    () => {
                        this.isLoading = false;
                        this.toastr.error("Add alert rule failed.");
                    }
                ));
        } else {
            // for the existing rule, it needs to call deleteAppRule first then call addAppRule
            this.subscriptions.push(this.alertService.deleteAppRule(this.appId, oldRuleName)
                .subscribe(
                    () => {
                        this.subscriptions.push(this.alertService.addAppRule(this.appId, newRuleName)
                            .subscribe(
                                () => {
                                    this.getRuleList();
                                    this.toastr.success("Update alert rule successful.");
                                },
                                () => {
                                    this.isLoading = false;
                                    this.toastr.error("Update alert rule failed.");
                                }
                            ));
                    },
                    () => {
                        this.isLoading = false;
                        this.toastr.error("Update alert rule failed.");
                    }
                ));
        }
    }

    doRuleDeletion() {
        this.isLoading = true;
        this.subscriptions.push(
            this.alertService.deleteAppRule(this.appId, this.deletingRule.ruleName)
            .subscribe(
                () => {
                    this.closeDeleteRuleConfirmation();
                    this.getRuleList();
                    this.toastr.success("Delete alert rule successful.");
                },
                () => {
                    this.isLoading = false;
                    this.closeDeleteRuleConfirmation();
                    this.toastr.error("Delete alert rule failed.");
                }
            )
        );
    }

    confirmDeleteRule(rule: ITCIRule) {
        if (rule.ruleName === "") {
            this.deleteEmptyRule();
        } else {
            this.deletingRule = rule;
            this.showAndCenterDeleteRuleDialog();
        }
    }

    closeDeleteRuleConfirmation() {
        this.showDeleteRuleConfirmation = false;
        this.deletingRule = null;
    }

    getRuleTypeLabel(ruleGroup: string) {
        switch (ruleGroup) {
            case ALERT_TYPE.TIB_APP_CPU:
                return ALERT_TYPE_LABEL.TIB_APP_CPU;
            case ALERT_TYPE.TIB_APP_MEMORY:
                return ALERT_TYPE_LABEL.TIB_APP_MEMORY;
            case ALERT_TYPE.TIB_APP_HIGH_FAULTED_EXECUTIONS:
                return ALERT_TYPE_LABEL.TIB_APP_HIGH_FAULTED_EXECUTIONS;
            case ALERT_TYPE.TIB_APP_INSTANCE_COUNT_0:
                return ALERT_TYPE_LABEL.TIB_APP_INSTANCE_COUNT_0;
            // case ALERT_TYPE.TIB_APP_XYZ:
            //     return ALERT_TYPE_LABEL.TIB_APP_XYZ;
        }
    }

    private formatRuleTypeList(ruleList: ITCIRule[]) {
        this.ruleTypeList = lodash.cloneDeep(GAL_RULE_TYPE_LIST).filter(rule => {
            return this.getUsableThresholdDropdownList(ruleList, rule.value as ALERT_TYPE).length;
        });
    }

    private formatRuleList(ruleList: ITCIRule[]): ITCIRule[] {
        ruleList.map(rule => {
            const dropdownList: SelectItem[] = [];
            ruleList
                .filter((item) => {
                    return rule.ruleName && item.ruleName === rule.ruleName || (item.ruleGroup === rule.ruleGroup && !item.isEnabled);
                })
                .forEach(item => {
                    dropdownList.push({
                        label: item.threshold,
                        value: item.threshold
                    });
                });
            rule.thresholdList = dropdownList;
            return rule;
        });
        return ruleList;
    }

    private getUsableThresholdDropdownList(ruleList: ITCIRule[], ruleGroup: ALERT_TYPE): SelectItem[] {
        const dropdownList: SelectItem[] = [];
        ruleList.filter(rule => rule.ruleGroup === ruleGroup && rule.isEnabled === false)
            .forEach(rule => {
                dropdownList.push({
                    label: rule.threshold,
                    value: rule.threshold
                });
            });
        return dropdownList;
    }

    private deleteEmptyRule() {
        lodash.remove(this.ruleList, (item) => {
            return item.ruleName === "";
        });
    }

    private isRuleUnsaved(): boolean {
        return this.ruleList.findIndex(item => item.ruleName === "") !== -1;
    }

    private showAndCenterDeleteRuleDialog() {
        this.showDeleteRuleConfirmation = true;
        this.dialogDeleteRule.center();
    }
    // --- for rule tab: end---


    // --- for hook tab: start---
    getHookList() {
        this.isLoading = true;
        this.subscriptions.push(
            this.alertService.getHookList()
                .subscribe(
                    (hookList: ITCIHook[]) => {
                        this.hookList = hookList;
                        this.isLoading = false;
                    },
                    () => {
                        this.isLoading = false;
                    }
                )
        );
    }

    testHookConnection(hookId: string) {
        // ignore if it is testing
        if (this.isLoading) {
            return;
        }
        this.toastr.clear();
        this.isLoading = true;
        this.subscriptions.push(
            this.alertService.getHookStatus(hookId)
                .subscribe(
                    (res) => {
                        this.isLoading = false;
                        this.toastr.success(res.message);
                    },
                    () => {
                        this.isLoading = false;
                        this.toastr.error("Test alert hook connection failed.");
                    }
                )
        );
    }

    addNewHook() {
        this.editingHook = null;
        this.showAndCenterAddEditHookDialog();
    }

    editHook(hook: ITCIHook) {
        this.editingHook = lodash.cloneDeep(hook);
        this.showAndCenterAddEditHookDialog();
    }

    saveHookDialog() {
        if (this.validateHook(this.editingHook)) {
            this.closeAddEditHookDialog();
        }
    }

    closeAddEditHookDialog() {
        this.showAddEditHookDialog = false;
        this.editingHook = null;
        this.toastr.clear();
    }
    doHookDeletion() {
        this.isLoading = true;
        this.subscriptions.push(
            Observable.of(null)
                .subscribe(
                    () => {
                        this.closeDeleteHookConfirmation();
                        this.getHookList();
                        this.toastr.success("Delete alert hook successful.");
                    },
                    () => {
                        this.isLoading = false;
                        this.closeDeleteHookConfirmation();
                        this.toastr.error("Delete alert hook failed.");
                    }
                )
        );
    }
    confirmDeleteHook(hook: ITCIHook) {
        this.deletingHook = hook;
        this.showAndCenterDeleteHookDialog();
    }
    closeDeleteHookConfirmation() {
        this.showDeleteHookConfirmation = false;
        this.deletingHook = null;
    }

    private validateHook(hook: ITCIHook) {
        let isValid = true;
        const hookName = hook?.name && hook.name.trim() || "";
        const hookUrl = hook?.url && hook.url.trim() || "";
        if (hookName === "") {
            isValid = false;
            this.toastr.error("Hook name is required.");
        } else if (hookUrl === "") {
            isValid = false;
            this.toastr.error("Hook url is required.");
        }
        return isValid;
    }

    private showAndCenterDeleteHookDialog() {
        this.showDeleteHookConfirmation = true;
        this.dialogDeleteHook.center();
    }

    private showAndCenterAddEditHookDialog() {
        this.showAddEditHookDialog = true;
        this.dialogAddEditHook.center();
    }
    // --- for hook tab: end---


    // --- for notification tab: start---
    getNotificationList() {
        this.isLoading = true;
        this.subscriptions.push(
            this.alertService.getNotificationList(this.appId)
                .subscribe(
                    (notificationList: ITCINotification[]) => {
                        this.originalNotificationList = lodash.cloneDeep(notificationList);
                        this.isLoading = false;
                        this.refreshNotification(this.sortedBy, this.pageStart);
                    },
                    () => {
                        this.isLoading = false;
                        this.toastr.error("Get notification list failed.");
                    }
                )
        );
    }
    clearNotificationFilter(): void {
        this.searchNotificationFilterCtrl.setValue("");
        this.searchNotification();
    }

    searchNotification() {
        this.refreshNotification(this.sortedBy, 1);
    }

    toggleSortNotification(sortBy: string) {
        this.sortAsc = !this.sortAsc;
        this.refreshNotification(sortBy, this.pageStart);
    }

    onPageChange(page: number) {
        this.refreshNotification(this.sortedBy, page);
    }

    private refreshNotification(sortedBy: string, pageStart: number) {
        this.filterNotificationByKeyword();
        this.sortNotification(sortedBy);
        this.setPaginatedRows(pageStart);
    }

    private filterNotificationByKeyword() {
        let entries: ITCINotification[];
        if (this.searchNotificationFilterCtrl.value) {
            entries = this.originalNotificationList.filter(item => {
                const isFound = this.shouldBeKept(item.alertName) || this.shouldBeKept(item.instanceId);
                if (this.isInEnvPage()) {
                    return isFound || this.shouldBeKept(item.appId);
                } else {
                    return isFound;
                }
            });
        } else {
            entries = this.originalNotificationList;
        }
        this.notificationList = this.formatNotification(entries);
    }

    private sortNotification(sortBy: string) {
        this.sortedBy = sortBy;

        this.notificationList = this.notificationList.sort((a, b) => this.alphaNumSort(a[sortBy].toString(), b[sortBy].toString())).reverse();
        if (this.sortAsc) {
            this.notificationList.reverse();
        }
    }

    private setPaginatedRows(page: number) {
        this.pageStart = page;
        let part: ITCINotification[];
        if (page === 1) {
            part = this.notificationList.slice(0, this.pages);
        } else {
            part = this.notificationList.slice(this.pageStart, this.pageStart + this.pages);
        }
        this.pager.changePage(page);
        this.paginatedRows$.next(part);
    }

    private formatNotification(notificationList: ITCINotification[]): ITCINotification[] {
        const entries: ITCINotification[] = lodash.cloneDeep(notificationList);
        entries.map(item => {
            item.tenantType = TCITenantAppType.fromString(item.appType);
            return item;
        });
        return entries;
    }

    private alphaNumSort(comparison1: string, comparison2: string) {
        return comparison1.localeCompare(comparison2, "en", { numeric: true, caseFirst: "upper" });
    }

    private shouldBeKept(sInput: string): boolean {
        return this.searchNotificationFilterCtrl.value ?
            sInput
                .toLowerCase()
                .search(lodash.escapeRegExp(this.searchNotificationFilterCtrl.value.toLowerCase())) > -1 :
            true;
    }
    // --- for notification tab: end---
}
